<script setup lang="ts">
import {reactive, ref, watch} from "vue";
import {watchDebounced} from "@vueuse/core";
import {RouterLink, useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {Avatar, Button, Card, Icon, IconField, InputIcon, InputText, MultiSelect, Tag} from "@/Components/Library";
import {getMemberGroups as apiGetMemberGroups} from "@/Services/Admin/admin-groups";
import {useMainStore} from "@/Stores/store";
import AddUserToGroupsDialog from "@/Pages/Admin/Groups/AddUserToGroupsDialog.vue";
import {getting} from "@/Composables/api";
import {PageLoading} from "@/Components/Global";
import {removeGroupMember} from "@/Services/Admin/admin-group-members";

const $store = useMainStore();
const confirm = useConfirm();
const router = useRouter();

const props = defineProps<{
    user: any;
}>();

const state = reactive({
    show: {
        addGroups: false,
    },
    filters: {
        search: null,
        member_roles: null,
    },
});
const search = ref(null);

const groups = ref([]);

const getGroups = async () => {
    apiGetMemberGroups(props.user.id, {
        filter: {
            search: search.value,
            hasOwnRole: state.filters.member_roles,
        },
        include: ["members"],
    }).then(({data}) => {
        console.log(";data", data);
        groups.value = data;
    });
};
getGroups();

const removeFromGroup = async (groupId: string) => {
    confirm.require({
        message: "Are you sure you want to remove this user from the group?",
        accept: async () => {
            await removeGroupMember(groupId, props.user.id);
            $store.toastMessages.push({
                severity: "success",
                summary: "Removed from group",
                detail: "The user has been removed from the group successfully.",
                life: 3000,
            });
            groups.value = groups.value.filter((group) => group.id !== groupId);
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

watch(
    state.filters,
    (filters) => {
        if (search.value !== state.filters.search) {
            return;
        }
        getGroups();
    },
    {deep: true},
);

watchDebounced(search, getGroups, {debounce: 500, deep: true});
</script>
<template>
    <div>
        <Card>
            <template #title>
                <div class="mb-2 flex items-end gap-2">
                    <span>Groups</span>
                    <div class="ml-auto flex">
                        <Button
                            class="h-10 w-10 border bg-stone-100 hover:bg-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
                            :icon="{
                                icon: 'mdi:plus',
                                width: 24,
                            }"
                            @click="state.show.addGroups = true"
                        />
                    </div>
                </div>
            </template>
            <div class="mb-4 flex flex-col items-center gap-2 lg:flex-row">
                <!-- Search -->
                <IconField class="w-full grow">
                    <InputIcon>
                        <Icon icon="prime:search" />
                    </InputIcon>
                    <InputText
                        id="search_groups"
                        type="search"
                        v-model="search"
                        placeholder="Search groups..."
                        class="!py-1.5 pl-10"
                        fluid
                    />
                    <InputIcon class="prime:times-circle" @click="search = null" />
                </IconField>
                <!-- Filters -->
                <div class="flex w-full lg:max-w-60">
                    <MultiSelect
                        class="w-full grow"
                        v-model="state.filters.member_roles"
                        placeholder="Filter by role"
                        :options="[
                            {label: 'Owner', value: 'owner'},
                            {label: 'Admin', value: 'admin'},
                            {label: 'Member', value: 'member'},
                        ]"
                        option-label="label"
                        option-value="value"
                        pt:label:class="px-2 py-1.5"
                        pt:dropdown:class="flex items-center pr-2"
                        pt:header:class="hidden"
                    />
                </div>
            </div>
            <PageLoading v-if="getting.includes('groups/member')" />
            <div
                v-if="!getting.includes('groups/member') && groups.length"
                class="flex flex-col divide-y dark:divide-stone-700"
            >
                <template v-for="group in groups" :key="group.id">
                    <div
                        class="relative flex w-full transition-colors duration-300 hover:bg-stone-50 dark:hover:bg-stone-950"
                    >
                        <RouterLink class="grow" :to="{name: 'admin.groups.show', params: {group: group.id}}">
                            <div class="flex w-full items-center gap-2 py-2.5 pr-2">
                                <Avatar
                                    :label="group.initials"
                                    class="h-10 w-10 shrink-0 !rounded-lg p-2 text-white"
                                    :class="[
                                        `bg-${group.color?.class}`,
                                        group.color?.class === 'white' ? 'text-black' : 'text-white',
                                    ]"
                                />
                                <h3 class="grow">
                                    {{ group.name }}
                                </h3>
                                <div class="ml-auto flex items-center gap-2">
                                    <Tag :value="group.members.length.toString()">
                                        <template #icon>
                                            <Icon icon="prime:users" width="16" class="mr-1" />
                                        </template>
                                    </Tag>
                                    <Icon v-if="group.user_id === user.id" icon="mdi:shield-check" width="24" />
                                    <Icon
                                        v-else-if="group.members.find((m) => m.id === user.id).role === 'admin'"
                                        icon="mdi:shield-account"
                                        width="24"
                                    />
                                </div>
                            </div>
                        </RouterLink>
                        <div class="flex items-stretch">
                            <Button
                                class="rounded-none !pl-1 !pr-4 hover:bg-stone-200"
                                :disabled="group.user_id === user.id"
                                :icon="{
                                    icon: 'mdi:close',
                                    width: 20,
                                }"
                                @click="removeFromGroup(group.id)"
                            />
                        </div>
                    </div>
                </template>
            </div>
            <div v-else>No groups found.</div>
        </Card>
        <AddUserToGroupsDialog v-model:visible="state.show.addGroups" :currentUser="user" @added="getGroups" />
    </div>
</template>
<style scoped></style>
